import React, { useEffect, useMemo, useState } from "react";
import adminService from "../../services/adminService";

const safe = (v, fallback = "-") => (v === null || v === undefined || v === "" ? fallback : v);

const formatDate = (d) => {
  if (!d) return "-";
  try {
    return new Date(d).toLocaleString("fr-FR");
  } catch {
    return String(d);
  }
};

const HotelReservationAdminModal = ({ reservation, onClose, onUpdated }) => {
  const reservationId = reservation?._id;

  // email affiché (lecture)
  const clientEmailRead =
    reservation?.client?.email ||
    reservation?.email ||
    reservation?.utilisateur?.email ||
    reservation?.compte?.email;

  const assignedHotel = reservation?.hotel && typeof reservation.hotel === "object" ? reservation.hotel : null;

  const initialPrice =
    reservation?.prixFinal ??
    reservation?.prix_total ??
    reservation?.prix ??
    reservation?.montant ??
    "";

  const initialPaymentUrl = reservation?.paymentUrl || reservation?.lienPaiement || reservation?.payment_link || "";

  const [prix, setPrix] = useState(initialPrice ? String(initialPrice) : "");
  const [savingPrice, setSavingPrice] = useState(false);

  const [paymentUrl, setPaymentUrl] = useState(initialPaymentUrl);
  const [generatingLink, setGeneratingLink] = useState(false);

  // ---------------------------
  // ✅ Infos client modifiables
  // ---------------------------
  const initialClientName =
    reservation?.client?.nomComplet ||
    reservation?.client?.nom ||
    reservation?.nom ||
    reservation?.username ||
    reservation?.compte?.username ||
    "";

  const initialClientPhone =
    reservation?.client?.telephone ||
    reservation?.telephone ||
    reservation?.compte?.telephone ||
    "";

  const initialClientAddress =
    reservation?.client?.adresse ||
    reservation?.adresse ||
    "";

  const initialClientEmailEditable =
    reservation?.email ||
    reservation?.client?.email ||
    reservation?.utilisateur?.email ||
    reservation?.compte?.email ||
    "";

  const [isEditingClient, setIsEditingClient] = useState(false);
  const [savingClient, setSavingClient] = useState(false);

  const [clientName, setClientName] = useState(initialClientName);
  const [clientEmail, setClientEmail] = useState(initialClientEmailEditable);
  const [clientPhone, setClientPhone] = useState(initialClientPhone);
  const [clientAddress, setClientAddress] = useState(initialClientAddress);

  const resetClientForm = () => {
    setClientName(initialClientName);
    setClientEmail(initialClientEmailEditable);
    setClientPhone(initialClientPhone);
    setClientAddress(initialClientAddress);
  };

  const handleSaveClientInfo = async () => {
    if (!reservationId) return;

    // minimal validation
    if (clientEmail && !String(clientEmail).includes("@")) {
      return alert("Email invalide");
    }

    try {
      setSavingClient(true);

      // ✅ On met à jour la réservation (top-level) : nom/email/telephone/adresse
      // (Sans toucher compte.* qui vient de la collection Compte)
      const payload = {
        nom: clientName || undefined,
        email: clientEmail || undefined,
        telephone: clientPhone || undefined,
        adresse: clientAddress || undefined,
      };

      // nettoie undefined
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

      await adminService.updateItem("reservationHotels", reservationId, payload);

      setIsEditingClient(false);
      await onUpdated?.();
      alert("Informations client mises à jour ✅");
    } catch (e) {
      console.error(e);
      alert("Erreur lors de la mise à jour des infos client");
    } finally {
      setSavingClient(false);
    }
  };

  // ---------------------------
  // Email
  // ---------------------------
  const defaultMessage = useMemo(() => {
    return `Bonjour,

Nous avons traité votre demande de réservation.
Veuillez effectuer le paiement via le lien sécurisé suivant :
[LIEN_PAIEMENT]

Cordialement,
Nouzha Hotel`;
  }, []);

  const [emailSubject, setEmailSubject] = useState("Lien de paiement - Réservation");
  const [emailMessage, setEmailMessage] = useState(defaultMessage);
  const [sendingEmail, setSendingEmail] = useState(false);

  // si reservation change (re-open modal)
  useEffect(() => {
    setPrix(initialPrice ? String(initialPrice) : "");
    setPaymentUrl(initialPaymentUrl || "");

    // reset client editable
    setClientName(initialClientName);
    setClientEmail(initialClientEmailEditable);
    setClientPhone(initialClientPhone);
    setClientAddress(initialClientAddress);
    setIsEditingClient(false);
  }, [reservationId]); // eslint-disable-line

  const handleSavePrice = async () => {
    const numeric = Number(prix);
    if (!numeric || numeric <= 0) return alert("Prix invalide");

    try {
      setSavingPrice(true);
      await adminService.updateHotelReservationPrice(reservationId, numeric);
      await onUpdated?.();
      alert("Prix enregistré ✅");
    } catch (e) {
      console.error(e);
      alert("Erreur lors de l'enregistrement du prix");
    } finally {
      setSavingPrice(false);
    }
  };

  const handleGeneratePaymentLink = async () => {
    const numeric = Number(prix);
    if (!numeric || numeric <= 0) return alert("Définis un prix d'abord");

    try {
      setGeneratingLink(true);
      const res = await adminService.generateHotelReservationPaymentLink(reservationId, numeric);
      const url = res?.data?.paymentUrl || res?.paymentUrl || res?.url;
      if (!url) throw new Error("paymentUrl manquant");
      setPaymentUrl(url);
      await onUpdated?.();
      alert("Lien généré ✅");
    } catch (e) {
      console.error(e);
      alert("Erreur lors de la génération du lien");
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleSendEmail = async () => {
    if (!paymentUrl) return alert("Génère d'abord un lien de paiement");

    // on préfère l'email modifié s'il existe
    const effectiveEmail = clientEmail || clientEmailRead;
    if (!effectiveEmail) return alert("Email client introuvable");

    try {
      setSendingEmail(true);
      await adminService.sendHotelReservationPaymentEmail(reservationId, emailSubject, emailMessage);
      await onUpdated?.();
      alert("Email envoyé ✅");
    } catch (e) {
      console.error(e);
      alert("Erreur lors de l'envoi email");
    } finally {
      setSendingEmail(false);
    }
  };

  const copyLink = async () => {
    if (!paymentUrl) return;
    await navigator.clipboard.writeText(paymentUrl);
    alert("Lien copié ✅");
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[80] flex items-center justify-center p-4 pt-20 overflow-y-auto">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-5xl w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-bold">Détails de la réservation hôtel</h3>
              <p className="text-white/90 text-xs">ID: {safe(reservationId)}</p>
            </div>
            <button onClick={onClose} className="text-white hover:text-slate-200">
              ✖
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[70vh] space-y-8">
          {/* Infos demande */}
          <div>
            <h4 className="text-lg font-semibold text-slate-900 border-b pb-2 mb-4">Demande</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-slate-50 p-4 rounded-xl">
                <div className="text-xs text-slate-500">Ville</div>
                <div className="font-semibold">{safe(reservation?.ville)}</div>
              </div>
              <div className="bg-slate-50 p-4 rounded-xl">
                <div className="text-xs text-slate-500">Étoiles demandées</div>
                <div className="font-semibold">{safe(reservation?.categorieEtoiles ?? reservation?.nb_etoiles)}</div>
              </div>

              <div className="bg-slate-50 p-4 rounded-xl">
                <div className="text-xs text-slate-500">Date début</div>
                <div className="font-semibold">{formatDate(reservation?.date_debut || reservation?.dateArrivee)}</div>
              </div>
              <div className="bg-slate-50 p-4 rounded-xl">
                <div className="text-xs text-slate-500">Date fin</div>
                <div className="font-semibold">{formatDate(reservation?.date_fin || reservation?.dateDepart)}</div>
              </div>

              <div className="bg-slate-50 p-4 rounded-xl">
                <div className="text-xs text-slate-500">Nb personnes</div>
                <div className="font-semibold">{safe(reservation?.nb_personnes || reservation?.nbInvites)}</div>
              </div>

              <div className="bg-slate-50 p-4 rounded-xl">
                <div className="text-xs text-slate-500">Créée le</div>
                <div className="font-semibold">{formatDate(reservation?.createdAt || reservation?.date_creation)}</div>
              </div>
            </div>
          </div>

          {/* ✅ Client (modifiable) */}
          <div>
            <div className="flex items-center justify-between gap-3 border-b pb-2 mb-4">
              <h4 className="text-lg font-semibold text-slate-900">Client</h4>

              {!isEditingClient ? (
                <button
                  onClick={() => setIsEditingClient(true)}
                  className="px-3 py-2 rounded-lg border border-orange-300 text-orange-700 hover:bg-orange-50 transition"
                >
                  ✏️ Modifier
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      resetClientForm();
                      setIsEditingClient(false);
                    }}
                    disabled={savingClient}
                    className="px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 transition disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSaveClientInfo}
                    disabled={savingClient}
                    className="px-3 py-2 rounded-lg bg-orange-600 text-white hover:bg-orange-700 transition disabled:opacity-50"
                  >
                    {savingClient ? "Sauvegarde..." : "💾 Sauvegarder"}
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Nom */}
              <div className="bg-orange-50 p-4 rounded-xl border border-orange-100">
                <div className="text-xs text-slate-500 mb-1">Nom</div>
                {!isEditingClient ? (
                  <div className="font-semibold">{safe(clientName)}</div>
                ) : (
                  <input
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    className="w-full px-3 py-2 border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Nom complet"
                  />
                )}
              </div>

              {/* Email */}
              <div className="bg-orange-50 p-4 rounded-xl border border-orange-100">
                <div className="text-xs text-slate-500 mb-1">Email</div>
                {!isEditingClient ? (
                  <div className="font-semibold text-orange-700">{safe(clientEmail || clientEmailRead)}</div>
                ) : (
                  <input
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="email@exemple.com"
                  />
                )}
              </div>

              {/* Téléphone */}
              <div className="bg-orange-50 p-4 rounded-xl border border-orange-100">
                <div className="text-xs text-slate-500 mb-1">Téléphone</div>
                {!isEditingClient ? (
                  <div className="font-semibold">{safe(clientPhone)}</div>
                ) : (
                  <input
                    value={clientPhone}
                    onChange={(e) => setClientPhone(e.target.value)}
                    className="w-full px-3 py-2 border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="+212..."
                  />
                )}
              </div>

              {/* Adresse */}
              <div className="bg-orange-50 p-4 rounded-xl border border-orange-100">
                <div className="text-xs text-slate-500 mb-1">Adresse</div>
                {!isEditingClient ? (
                  <div className="font-semibold">{safe(clientAddress)}</div>
                ) : (
                  <input
                    value={clientAddress}
                    onChange={(e) => setClientAddress(e.target.value)}
                    className="w-full px-3 py-2 border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Adresse..."
                  />
                )}
              </div>

              {isEditingClient && (
                <p className="md:col-span-2 text-xs text-slate-500">
                  Note: ces champs sont enregistrés sur la réservation (reservationHotels) pour rester compatibles même si
                  le client vient d’un compte (Compte) ou d’un formulaire invité.
                </p>
              )}
            </div>
          </div>

          {/* Hôtel assigné */}
          <div>
            <h4 className="text-lg font-semibold text-slate-900 border-b pb-2 mb-4">Hôtel assigné</h4>

            {assignedHotel ? (
              <div className="bg-green-50 p-5 rounded-xl border border-green-100">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                  <div className="md:col-span-2">
                    <div className="text-xs text-slate-500">Nom</div>
                    <div className="text-lg font-bold">{safe(assignedHotel.nom)}</div>

                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-xs text-slate-500">Ville</div>
                        <div className="font-semibold">{safe(assignedHotel.ville)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Étoiles</div>
                        <div className="font-semibold">{safe(assignedHotel.nb_etoiles)}</div>
                      </div>
                    </div>

                    <div className="mt-3 text-xs text-slate-500">ID hôtel: {safe(assignedHotel._id)}</div>
                  </div>

                  {assignedHotel.image && (
                    <div className="flex justify-center">
                      <img
                        src={
                          assignedHotel.image.startsWith("http")
                            ? assignedHotel.image
                            : `https://nouzhaback-theta.vercel.app${assignedHotel.image}`
                        }
                        alt="Hotel"
                        className="w-full max-w-[220px] h-28 object-cover rounded-xl border border-slate-200"
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-slate-600">
                Aucun hôtel assigné pour le moment.
              </div>
            )}
          </div>

          {/* Prix + paiement */}
          <div className="bg-blue-50 p-6 rounded-xl border border-blue-100">
            <h4 className="text-lg font-semibold text-blue-900 mb-4">Tarification & Paiement</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Prix */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Prix (MAD)</label>
                <input
                  type="number"
                  value={prix}
                  onChange={(e) => setPrix(e.target.value)}
                  placeholder="Définir le prix final"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={handleSavePrice}
                  disabled={savingPrice || !prix}
                  className="mt-2 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {savingPrice ? "Sauvegarde..." : "💾 Sauvegarder le prix"}
                </button>
              </div>

              {/* Lien paiement */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Lien de paiement</label>
                <div className="flex">
                  <input
                    type="text"
                    value={paymentUrl}
                    readOnly
                    placeholder="Générer un lien de paiement"
                    className="w-full px-4 py-3 border border-slate-300 rounded-l-lg bg-slate-50"
                  />
                  <button
                    onClick={copyLink}
                    disabled={!paymentUrl}
                    className="px-4 py-2 bg-slate-200 text-slate-700 rounded-r-lg hover:bg-slate-300 transition-colors disabled:opacity-50"
                    title="Copier"
                  >
                    📋
                  </button>
                </div>
                <button
                  onClick={handleGeneratePaymentLink}
                  disabled={generatingLink || !prix || Number(prix) <= 0}
                  className="mt-2 w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {generatingLink ? "Génération..." : "🔗 Générer lien paiement"}
                </button>
              </div>
            </div>
          </div>

          {/* Email */}
          <div className="bg-green-50 p-6 rounded-xl border border-green-100">
            <h4 className="text-lg font-semibold text-green-900 mb-4">Envoyer email au client</h4>

            <div className="grid grid-cols-1 gap-3">
              <input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                className="w-full px-4 py-3 border border-green-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Sujet"
              />

              <textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                className="w-full px-4 py-3 border border-green-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent min-h-[180px] font-mono text-sm"
                placeholder="Message..."
              />

              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="text-xs text-green-800">
                  <p>- Utilise <strong>[LIEN_PAIEMENT]</strong> pour insérer le lien automatiquement</p>
                  <p>- Email destinataire : <strong>{safe(clientEmail || clientEmailRead)}</strong></p>
                </div>

                <button
                  onClick={handleSendEmail}
                  disabled={sendingEmail || !paymentUrl}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {sendingEmail ? "Envoi..." : "📧 Envoyer email"}
                </button>
              </div>

              {!paymentUrl && (
                <p className="text-sm text-red-600">Générez d’abord un lien de paiement pour pouvoir envoyer l’email.</p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-50 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-100 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default HotelReservationAdminModal;
